/**
 * @file The workspace frame: sidebar on a laptop, drawer on a phone.
 *
 * ## Why this replaced a centred column
 *
 * Measured on the previous layout: a fixed 768px content column is 60% of a 1280
 * laptop and 53% of a 1440 one, leaving 256–336px of dead gutter each side. A
 * property-management app is used on a laptop for the work that matters (rent
 * cycle, ledger, reports), so the frame now uses the width it is given and keeps
 * a persistent rail for navigation.
 *
 * ## Mobile is not an afterthought
 *
 * Below `lg` the rail collapses into a top bar with a menu button that opens the
 * SAME `AppSidebar` inside a Sheet — one component, one nav map, two placements,
 * so they cannot drift. The top bar also carries the workspace name, because on a
 * phone the sidebar is hidden and that name is the only answer to "where am I".
 *
 * `h-dvh` (not `h-screen`) so mobile browser chrome does not push the sidebar's
 * footer off-screen.
 */
import { useState } from "react";
import { Outlet } from "@tanstack/react-router";
import { Menu } from "lucide-react";
import { AppSidebar } from "./AppSidebar";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "./ui/sheet";
import type { ApiWorkspace } from "../api/types";

export function WorkspaceLayout({ workspace }: { readonly workspace: ApiWorkspace }) {
  const [navOpen, setNavOpen] = useState(false);

  return (
    <div className="flex min-h-dvh flex-col lg:flex-row">
      {/* Desktop rail. */}
      <aside className="hidden w-64 shrink-0 border-r border-border bg-card lg:block">
        <div className="sticky top-0 h-dvh">
          <AppSidebar workspace={workspace} />
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Mobile bar + drawer. The always-present brand bar is in AppShell, so
            this bar carries only what is workspace-specific: the menu and the
            portfolio name (the sidebar is hidden on a phone, and the name is the
            only answer to "where am I"). */}
        <div role="navigation" aria-label="Workspace" className="flex items-center gap-2 border-b border-border bg-card px-3 py-2 lg:hidden">
          <Sheet open={navOpen} onOpenChange={setNavOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Open navigation">
                <Menu className="size-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0">
              <SheetTitle className="sr-only">Navigation</SheetTitle>
              <AppSidebar workspace={workspace} onNavigate={() => setNavOpen(false)} />
            </SheetContent>
          </Sheet>
          <span className="min-w-0 flex-1 truncate text-sm font-medium">
            {workspace.name}
          </span>
        </div>

        {/*
          `max-w-none` on purpose: the previous frame centred a narrow column, and
          the tables this app is heading towards (ledger, rent roll) need the
          width. Padding carries the breathing room instead of a column cap.
        */}
        <main className="min-w-0 flex-1 px-4 py-6 sm:px-6 lg:px-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
